import { formatDate } from '@fullcalendar/core';
import { CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTimeline } from '../../../../sic-lib/api/hooks/useTimeline';
import { ChannelItemsResponse } from '../../../../sic-lib/api/Timeline/types/channelItemsResponse';
import { ChannelItemUnion } from '../../../../sic-lib/types/ChannelItem';
import { Contact } from '../../../../sic-lib/types/Contact';
import { groupMessagesByDate } from '../../../../sic-lib/utils/channelTimelineHelper';
import { filterSelectedChannelTypes } from '../../../../sic-lib/utils/timelineHelpers';
import { useAppSelector } from '../../../../store/hooks';
import PageError from '../../../Navigation/PageError/PageError';
import ConversationListItem from '../ConversationListItem/ConversationListItem';

export default React.memo(function ConversationList({ contact, conversationComposerSize }: Props) {
    const { classes } = useStyles();
    const queryClient = useQueryClient();
    const topSentinelRef = React.useRef<HTMLDivElement | null>(null);
    const bottomSentinelRef = React.useRef<HTMLDivElement | null>(null);

    const channels = useAppSelector(state => state.conversation.channels);
    const clientId = useAppSelector(state => state.user.clientId);
    const userRoles = useAppSelector(state => state.user.userRoles);
    const users = useAppSelector(state => state.user.users);

    let { data, isError, isLoading, hasNextPage, fetchNextPage, isFetching } = useTimeline(
        contact.contactId,
        filterSelectedChannelTypes(channels),
    );

    const channelItems = React.useMemo(
        () => groupMessagesByDate(data?.pages.map(page => page.items).flat() || []),
        [data],
    );

    React.useEffect(() => {
        //infinite loading detection
        let topObserver: IntersectionObserver | null = null;
        if (topSentinelRef.current) {
            topObserver = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting) {
                        //if (!isFetching && hasNextPage)
                        fetchNextPage({
                            cancelRefetch: false, //don't cancel the previous fetch (scrolling up and down quickly)
                        });
                    }
                },
                { rootMargin: '500px' },
            );

            topObserver.observe(topSentinelRef.current);
        }
        //bottom reached detection - reset pagination
        let bottomObserver: IntersectionObserver | null = null;
        if (bottomSentinelRef.current) {
            bottomObserver = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting) {
                        const selectedChannels = filterSelectedChannelTypes(channels);
                        //stop paging from reverting this setQueryData
                        queryClient.cancelQueries({
                            queryKey: [
                                `contact-${contact.contactId}`,
                                'timeline',
                                selectedChannels,
                            ],
                        });
                        queryClient.setQueryData<InfiniteData<ChannelItemsResponse>>(
                            [`contact-${contact.contactId}`, 'timeline', selectedChannels],
                            oldData => {
                                if (oldData) {
                                    return {
                                        pageParams: oldData.pageParams.slice(0, 1),
                                        pages: oldData.pages.slice(0, 1),
                                    };
                                }
                                return oldData;
                            },
                        );
                    }
                },
                { rootMargin: '500px' },
            );

            bottomObserver.observe(bottomSentinelRef.current);
        }
        return () => {
            if (topSentinelRef.current && topObserver)
                topObserver.unobserve(topSentinelRef.current);
            if (bottomSentinelRef.current && bottomObserver)
                bottomObserver.unobserve(bottomSentinelRef.current);
        };
    }, [hasNextPage, topSentinelRef]);

    if (isError) {
        return (
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                className={classes.flexWrapper}
            >
                <PageError />
            </Stack>
        );
    }

    if (isLoading) {
        return (
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                className={classes.flexWrapper}
            >
                <CircularProgress color="primary" />
            </Stack>
        );
    }

    return (
        <Stack
            direction="column-reverse"
            className={classes.flexWrapper}
            spacing={2}
            pb={`${conversationComposerSize + 100}px`}
        >
            <div ref={bottomSentinelRef}></div>
            {channelItems &&
                channelItems.map(item => {
                    return item.type === 'header' ? (
                        <Stack
                            key={(item.item as Date).toString()}
                            justifyContent="center"
                            alignItems="center"
                            my={2}
                        >
                            <Typography variant="caption" color="textSecondary">
                                {formatDate(item.item as Date)}
                            </Typography>
                        </Stack>
                    ) : (
                        <ConversationListItem
                            contact={contact}
                            channelItem={item.item as ChannelItemUnion}
                            clientId={clientId}
                            roles={userRoles}
                            users={users}
                            key={(item.item as ChannelItemUnion).id}
                        />
                    );
                })}
            <Stack ref={topSentinelRef}>
                <LinearProgress color="primary" sx={{ mb: 2, opacity: isFetching ? 1 : 0 }} />
            </Stack>
        </Stack>
    );
});

interface Props {
    contact: Contact;
    conversationComposerSize: number;
}

const useStyles = makeStyles()((theme: Theme) => ({
    flexWrapper: {
        height: '100%',
        overflow: 'auto',
        //paddingBottom: '200px',
        transition: 'padding-bottom 150ms ease-in-out',
    },
    floatingNewMsgBtn: {
        background: 'red',
        position: 'absolute',
        height: '200px',
    },
}));
